import { CircularProgress } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { NftInclude } from '@mogul/api-client';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { getNftIfMinted } from '../../api-helpers/nft';
import { ReactComponent as BlackStarIcon } from '../../common/svg/button-icons/black_star_icon.svg';
import { ReactComponent as WinnerIcon } from '../../common/svg/button-icons/winner_icon.svg';
import type { ProjectMetadata } from '../../containers/pages/project/ProjectPage.container';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../hooks';
import { useMovieVotingContract } from '../../hooks/movie-voting.hooks';
import type { MovieRound } from '../../hooks/voting.hooks';
import { useVotingContract } from '../../hooks/voting.hooks';
import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from '../../lib';
import { CollectUnclaimedRewardsModal } from '../common/modal/movie-voting/CollectUnclaimedRewardsModal.component';
import { Button, Colors } from '../legacy/button/Button.component';
import styles from './OtherRounds.module.css';

interface Props {
  rounds: MovieRound[];
  totalNumRounds: number;
  handleClaimRewards: (roundId: string) => Promise<boolean>;
  isFutureRounds: boolean;
  startingRoundNum: number;
}

export const OtherRounds: React.FC<Props> = props => {
  const {
    rounds,
    totalNumRounds,
    handleClaimRewards,
    isFutureRounds,
    startingRoundNum,
  } = props;

  const { walletDetails } = useWeb3State();
  const { setModal } = useContext(ModalStateContext);
  const history = useHistory();

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const { initialized, calculateStarsRewards } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const [totalRewards, setTotalRewards] = useState<number>(0);
  const [winningMovieIds, setWinningMovieIds] = useState<
    Record<string, string>
  >({});
  const [NFTMetaData, setNFTMetaData] = useState<
    Record<string, ProjectMetadata>
  >({});
  const [userStarsRewards, setUserStarsRewards] = useState<
    Record<string, string>
  >({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    void (async () => {
      for (const round of rounds) {
        const numberVotes = round.votes.map(vote => parseInt(vote, 10));
        const i = numberVotes.indexOf(Math.max(...numberVotes));
        const nft = await getNftIfMinted(
          WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingNft,
          round.movieIds[i],
          [NftInclude.Project],
        );

        const movieData = nft?.project;
        const result = {
          description: movieData?.description ?? '',
          genre: movieData?.genre ?? '',
          image: movieData?.posterImage?.publicUrl ?? '',
          name: movieData?.title ?? '',
          studio: movieData?.studio ?? '',
        };

        setNFTMetaData(prev => ({
          ...prev,
          [round.movieIds[i]]: result,
        }));
        setWinningMovieIds(prev => ({
          ...prev,
          [round.roundId]: round.movieIds[i],
        }));

        const rewardsForRound = await calculateStarsRewards(round.roundId);
        setUserStarsRewards(prev => ({
          ...prev,
          [round.roundId]: rewardsForRound,
        }));
        setTotalRewards(prev => prev + parseFloat(rewardsForRound));
      }
      setLoading(false);
    })();
  }, [initialized]);

  const handleGoToRound = (roundId: string, placeInPastRounds: string) => {
    history.push(`/platform/voting/${roundId}/${placeInPastRounds}`);
  };

  const handleCollectStars = () =>
    setModal(
      CollectUnclaimedRewardsModal(
        rounds,
        startingRoundNum,
        totalNumRounds,
        userStarsRewards,
        NFTMetaData,
        handleClaimRewards,
      ),
    );

  if (loading) {
    return (
      <div className={styles.pastRounds + ' rounded flex-col gap-1'}>
        <CircularProgress style={{ color: 'white' }} size={100} />
      </div>
    );
  }

  const previousRounds = rounds.map((round, i) => {
    const winningMovie = NFTMetaData[winningMovieIds[round.roundId]];

    return (
      <button
        key={`${winningMovie.name}${(i + 1).toString()}`}
        className={
          styles.previousRounds + ' flex items-center justify-between mb-3'
        }
        onClick={() =>
          handleGoToRound(
            round.roundId,
            (rounds.length - i + startingRoundNum).toString(),
          )
        }
      >
        <div className={'flex items-center'}>
          <img
            alt="winning project poster"
            className={'h-auto w-20 mr-5'}
            src={winningMovie.image}
          />
          <div>
            <div
              className={
                styles.previousRoundsHeader +
                ' flex items-center xl:text-3xl md:text-xl text-lg'
              }
            >
              {!isFutureRounds && <WinnerIcon className={'bottom-0.5 mr-2'} />}
              ROUND {rounds.length - i + startingRoundNum}
            </div>
            <div className="xl:text-4xl md:text-2xl text-xl">
              {winningMovie.name}
            </div>
          </div>
        </div>
        <ChevronRightIcon className={styles.rightChevron} />
      </button>
    );
  });

  return (
    <div className={styles.pastRounds + ' rounded flex-col gap-1'}>
      <div className={'flex items-center justify-between mb-3'}>
        <div className={styles.header + ' lg:text-5xl md:text-3xl text-xl'}>
          {isFutureRounds ? 'FUTURE ROUNDS' : 'PAST ROUNDS'}
        </div>
        {!isFutureRounds && (
          <Button
            className={'flex md:h-11 h-8'}
            color={Colors.COLOR_SCHEME_2}
            onClick={handleCollectStars}
            disabled={previousRounds.length === 0 || totalRewards === 0}
          >
            <BlackStarIcon className="mr-2" />
            <span className="lg:block hidden">COLLECT UNCLAIMED STARS</span>
            <span className="lg:hidden sm:block hidden text-sm">
              COLLECT STARS
            </span>
            <span className="sm:hidden text-sm">COLLECT</span>
          </Button>
        )}
      </div>
      {previousRounds}
    </div>
  );
};
